import { createNavigation } from 'next-intl/navigation'
import {
    permanentRedirect as nextPermanentRedirect,
    redirect as nextRedirect,
} from 'next/navigation'

import { isDefaultLocaleHomepage } from './homepage-href'
import { needsNoLocalePrefix } from './locale-prefix'
import { defaultLocale } from './locales'
import { routing } from './routing'

const nav = createNavigation(routing)

// `usePathname` only reads the current path, so it is re-exported unchanged.
export const { usePathname } = nav

// The shared default-locale-homepage decision (consumed by all four helpers); the
// `Link` and `useRouter` wrappers live in `'use client'` modules because each needs
// something only a client component has — a context read, the framework router —
// and are re-exported here so `@/i18n/navigation` stays the single import surface.
export { isDefaultLocaleHomepage } from './homepage-href'
export { Link } from './navigation-link'
export { useRouter } from './navigation-router'

export const getPathname: typeof nav.getPathname = (args) => {
    // An already-final href is its own pathname — see `needsNoLocalePrefix`. String
    // form only: a `UrlObject` would need its query and hash re-serialized, and
    // no caller passes one for these surfaces.
    if (typeof args.href === 'string' && needsNoLocalePrefix(args.href)) {
        return args.href
    }

    const pathname = nav.getPathname(args)

    if (isDefaultLocaleHomepage(args.href, args.locale)) {
        const bare = pathname.replace(new RegExp(`^/${defaultLocale}(?=/|\\?|$)`), '')
        return bare === '' || bare.startsWith('?') ? `/${bare}` : bare
    }

    return pathname
}

export const redirect: typeof nav.redirect = (args, type) => {
    // Default-locale homepage → the bare `/` (via the framework redirect, which is a
    // server function and does not prefix); everything else keeps next-intl's
    // locale-aware redirect.
    if (isDefaultLocaleHomepage(args.href, args.locale)) {
        nextRedirect('/', type)
    }
    return nav.redirect(args, type)
}

export const permanentRedirect: typeof nav.permanentRedirect = (args, type) => {
    if (isDefaultLocaleHomepage(args.href, args.locale)) {
        nextPermanentRedirect('/', type)
    }
    if (typeof args.href === 'string' && needsNoLocalePrefix(args.href)) {
        nextPermanentRedirect(args.href, type)
    }
    return nav.permanentRedirect(args, type)
}
